// The approval step open to the person looking (TX-US-0043): which step it is, what they read
// about the counterparty where counterparties are on (AC17), the earlier rounds, and Approve,
// Return and Reject - the last two with a reason. The API decides who may take a step; the panel
// is shown only to someone it names (`open_step_for_me`).

import { useState } from 'react';

import { decideDeal } from '../api.js';
import { gradeLabel } from '../counterparties/credit/sheet.js';
import { statusLabel } from '../counterparties/labels.js';
import { notify } from '../ui/notify.jsx';
import DealRounds from './DealRounds.jsx';
import { formatMoney } from './dealForm.js';
import { DECISIONS, STAGE_LABELS, STEP_LABELS, refusalOf } from './flow.js';
import ReasonDialog from './ReasonDialog.jsx';

const REASONED = {
  RETURNED: {
    title: 'Return the deal?',
    hint: 'It goes back to Proposal with your reason, and whoever submitted it is told.',
    confirmLabel: 'Return',
    busyLabel: 'Returning…',
  },
  REJECTED: {
    title: 'Reject the deal?',
    hint: 'A rejected deal is final. Whoever submitted it is told, with your reason.',
    confirmLabel: 'Reject',
    busyLabel: 'Rejecting…',
    danger: true,
  },
};

const NEXT = {
  COMMERCIAL: 'Approving opens the risk step.',
  RISK: 'Approving moves the deal to Contract.',
};

// `namedPlace` is the named place's label under the deal's Incoterm. A decision that succeeds
// leaves the panel busy: the deal is read again, and the panel comes back for its new version.
export default function DecisionPanel({ deal, view, events, namedPlace, onChanged }) {
  const step = view.open_step_for_me;
  const summary = view.counterparty_summary;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [asking, setAsking] = useState(null);

  const decide = async (decision, reason = null) => {
    const { deal: updated } = await decideDeal(
      deal.pre_deal_id, step, decision, deal.version, reason,
    );
    setAsking(null);
    // Every control stays disabled until the deal is read again and the panel comes back for
    // its new version - a dialog's decision as much as Approve.
    setBusy(true);
    notify(`${STEP_LABELS[step]} recorded. The deal is at ${STAGE_LABELS[updated.stage]}.`);
    onChanged();
  };

  const approve = async () => {
    setBusy(true);
    setError(null);
    try {
      await decide('APPROVED');
    } catch (err) {
      setError(refusalOf(err, namedPlace));
      setBusy(false);
    }
  };

  return (
    <section className="profile-panel decision-panel" aria-label="Your decision">
      <h3>{STEP_LABELS[step]} — round {deal.approval?.round}</h3>
      {deal.approval && (
        <p className="hint">
          Submitted by {deal.approval.submitted_by} on{' '}
          {new Date(deal.approval.submitted_at).toLocaleString()}.
          {deal.approval.commercial && ` Commercial step approved by ${deal.approval.commercial.by}.`}
        </p>
      )}

      {summary && (
        <dl className="deal-fields counterparty-summary">
          <dt>Counterparty</dt>
          <dd>{summary.name}</dd>
          <dt>Onboarding status</dt>
          <dd>{statusLabel(summary.status)}</dd>
          <dt>Internal rating</dt>
          <dd>
            {summary.internal_rating
              ? gradeLabel('internal_rating', summary.internal_rating)
              : 'No credit assessment yet'}
          </dd>
          <dt>Overall risk</dt>
          <dd>
            {summary.overall_risk ? gradeLabel('overall_credit_risk', summary.overall_risk) : '—'}
          </dd>
          <dt>Counterparty limit</dt>
          <dd>{summary.counterparty_limit ? formatMoney(summary.counterparty_limit) : '—'}</dd>
        </dl>
      )}

      <div className="status-actions-row">
        {DECISIONS.map(({ decision, label, reason }) => (
          <button key={decision} type="button" disabled={busy}
                  className={{ APPROVED: 'primary', REJECTED: 'danger' }[decision]}
                  onClick={reason ? () => setAsking(decision) : approve}>
            {busy && !reason ? 'Approving…' : label}
          </button>
        ))}
        <p className="hint">
          {NEXT[step]} Return sends it back to Proposal with your reason; Reject ends it.
        </p>
      </div>

      {error && (
        <p className="error">
          {error.message}
          {error.stale && (
            <> <button type="button" className="link-button" onClick={onChanged}>Reload</button></>
          )}
        </p>
      )}

      <h4>Rounds</h4>
      <DealRounds events={events} />

      {asking && (
        <ReasonDialog {...REASONED[asking]} onClose={() => setAsking(null)}
                      act={(reason) => decide(asking, reason)}
                      refusal={(err) => refusalOf(err, namedPlace).message} />
      )}
    </section>
  );
}
